import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { asHtml } from '../../helpers/markdown';
import Content from '../../styled-components/components/Content';
import { Image } from '../../styled-components/components/Image';
import { Link } from '../../styled-components/components/Link';
import Modal from '../../styled-components/components/Modal';
import { StackMedium } from '../../styled-components/components/Stack';

interface GuaranteePackageItemProps {
  title: string;
  image?: string;
  logo?: string;
  description?: string;
  linkUrl?: string;
  linkName?: string;
  cardType: string;
  index: number;
  onTrackClick: (cardType: string, name: string, position: number) => void;
  onTrackOutboundLinkClick: (cardType: string, name: string, position: number) => void;
  dangerousHtml?: boolean;
}

const GuaranteePackageItem = ({
  title,
  image,
  logo,
  description,
  linkUrl,
  linkName,
  cardType,
  index,
  onTrackClick,
  onTrackOutboundLinkClick,
  dangerousHtml,
}: GuaranteePackageItemProps) => {
  const { t } = useTranslation(['common']);
  const [tracked, setTracked] = useState(false);
  const [outboundTracked, setOutboundTracked] = useState(false);
  const [modal, setModal] = useState(false);
  const onToggleModal = useCallback(() => {
    setModal(!modal);
    if (!modal && !tracked) {
      setTracked(true);
      onTrackClick(cardType, title, index + 1);
    }
  }, [modal, tracked]);

  const _onTrackOutboundLinkClick = useCallback(() => {
    if (!outboundTracked) {
      setOutboundTracked(true);
      onTrackOutboundLinkClick(cardType, title, index + 1);
    }
  }, [outboundTracked]);

  return (
    <div data-wx-infocard="">
      <div className="info-card-header">
        <div className="info-card-title">{title}</div>
        {logo && (
          <div className="info-card-logo">
            <img
              src={`${logo}?h=25`}
              srcSet={`${logo}?h=50 2x`}
              alt={title}
              className="info-card-logo-img"
            />
          </div>
        )}
      </div>
      <div className="info-card-body">
        {description && dangerousHtml && (
          <div className="info-card-description">
            <div
              data-am-content=""
              dangerouslySetInnerHTML={{
                __html: asHtml(description),
              }}
            />
          </div>
        )}
        {description && !dangerousHtml && (
          <div className="info-card-description">
            <div data-am-content="">
              <p>{description}</p>
            </div>
          </div>
        )}
        <div className="info-card-link">
          <button
            type="button"
            data-am-link="clickable-parent"
            className="l-block"
            onClick={onToggleModal}
            title={t('Read more about this', { ns: 'common', name: title })}
          >
            {t('Read more', { ns: 'common' })}
          </button>
        </div>
      </div>
      {modal && (
        <Modal heading={title} onClose={onToggleModal}>
          {image && (
            <StackMedium>
              <Image $fullWidth src={`${image}?w=570`} alt={title} />
            </StackMedium>
          )}
          {description && dangerousHtml && (
            <StackMedium>
              <Content
                dangerouslySetInnerHTML={{
                  __html: asHtml(description),
                }}
              />
            </StackMedium>
          )}
          {description && !dangerousHtml && (
            <StackMedium>
              <Content>
                <p>{description}</p>
              </Content>
            </StackMedium>
          )}
          {linkUrl && (
            <StackMedium>
              <Link
                href={linkUrl}
                onClick={_onTrackOutboundLinkClick}
                target="_blank"
                rel="nofollow noopener noreferrer"
                title={linkName || linkUrl}
              >
                {linkName || linkUrl}
              </Link>
            </StackMedium>
          )}
        </Modal>
      )}
    </div>
  );
};

export default GuaranteePackageItem;
